// @ts-nocheck
import React from "react";
import { RecipeSwiper } from "../components/RecipeSwiper";
import { DiscoverHeader } from "../components/discover/DiscoverHeader";
import { RecipeGrid } from "../components/discover/RecipeGrid";
import { RecipeDetailModal } from "../components/discover/RecipeDetailModal";
import { AnimatedSkeleton } from "../components/ui/animated-skeleton";
import { MealPlanErrorBoundary } from "../../components/MealPlanErrorBoundary";
import { AiErrorBoundary } from "../../components/AiErrorBoundary";
import type { PersonalizationPreviewResult } from "../../types/personalization";
import {
  toRecipeCardVMFromLegacy,
  toRecipeCardVMFromV2,
} from "../../shared/adapters/recipeCardVm";
import { UI_FLAGS as FLAGS } from "../../lib/flags";
import { Button } from "../components/ui/button";

// View-only types (mirrored from container)
interface Recipe {
  id: string;
  title: string;
  description?: string;
  servings?: number;
  cookTime?: number;
  prepTime?: number;
  difficulty?: string;
  category?: string;
  tags?: string[];
  ingredients?: string[];
  instructions?: string[];
  nutrition?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
  };
  imageUrl?: string;
  isAiGenerated?: boolean;
  sourceUrl?: string;
}

interface MealPlan {
  id: string;
  recipes: Recipe[];
  weekRange?: string;
  generatedAt?: Date;
}

interface UserProfile {
  diet?: string;
  allergies?: string[];
  preferences?: string[];
}

interface RecipePageViewProps {
  // Data
  recipes: Recipe[];
  currentMealPlan: MealPlan | null | undefined;
  currentMealPlanRecipes: Recipe[];
  userProfile: UserProfile | null | undefined;
  userTasteProfile: any;
  userName: string;
  normalizedTier: string;

  // Loading states
  recipesLoading: boolean;

  // Search and filter state
  searchQuery: string;
  selectedCategory: string | null;
  selectedTags: string[];
  sortBy: "relevance" | "cookTime" | "difficulty" | "title";
  viewMode: "grid" | "list";
  currentPage: number;
  hasMoreRecipes: boolean;

  // Filter options
  availableCategories: string[];
  availableTags: string[];

  // Modal state
  selectedRecipe: Recipe | null;
  replaceModalOpen: boolean;
  removeModalOpen: boolean;
  detailsModalOpen: boolean;
  recipeToReplace: Recipe | null;
  recipeToRemove: Recipe | null;

  // Computed values
  hasCurrentMealPlan: boolean;
  isRecipeInMealPlan: (recipeId: string) => boolean;
  totalRecipesCount: number;

  // Event handlers - Search and filter
  onSearch: (query: string) => void;
  onCategoryFilter: (category: string | null) => void;
  onTagFilter: (tags: string[]) => void;
  onSortChange: (
    sort: "relevance" | "cookTime" | "difficulty" | "title",
  ) => void;
  onViewModeChange: (mode: "grid" | "list") => void;
  onLoadMore: () => void;

  // Event handlers - Recipe interactions
  onRecipeClick: (recipe: Recipe) => void;
  onReplaceRecipe: (recipeToReplace: Recipe) => void;
  onConfirmReplace: (newRecipe: Recipe) => void;
  onRemoveRecipe: (recipe: Recipe) => void;
  onConfirmRemove: () => void;
  onAddToMealPlan: (recipe: Recipe) => void;
  onGenerateNewPlan: () => void;

  // Event handlers - Modal management
  onCloseModals: () => void;

  // Event handlers - Navigation
  onGoToDashboard: () => void;
  onGoToShoppingList: () => void;
  onSetupProfile: () => void;
}

// Additional props needed for existing RecipePage functionality
interface RecipePageViewExtendedProps extends RecipePageViewProps {
  // Extended state for original RecipePage compatibility
  refreshing: boolean;
  paginationCategory: string | undefined;
  loadingMore: boolean;
  creating: boolean;
  addingToPlan: boolean;
  likedRecipes: string[];
  activePreview: PersonalizationPreviewResult | null;
  previewLoading: boolean;
  previewError: string | null;
  hasMore: boolean;
  error: any;
  personalizedRecipes?: any[];
  personalizedLoading?: boolean;
  personalizedGroups?: Record<string, any[]>;

  // Extended event handlers for original RecipePage compatibility
  onRefreshRecipes: () => void;
  onTrack: (
    recipeId: string,
    action: "liked" | "skipped" | "saved" | "maybe",
  ) => void;
  onCreatePlan: (selected: any[]) => void;
  onLikeRecipe: (recipeId: string) => void;
  onCreatePlanFromGrid: () => void;
  onAddToPlanFromModal: (
    recipeId: string,
    dayIndex?: number,
    mealType?: string,
    preview?: PersonalizationPreviewResult | null,
  ) => void;
  onPreviewPersonalization: (recipeId: string) => void;
  onCreateCustomRecipe: () => void;
  onViewDetails: (recipeId: string) => void;
}

function PreferenceEmptyState({ onSetupProfile }) {
  return (
    <div className="text-center py-16">
      <h3 className="text-xl font-semibold mb-2">Personalize Your Discover Feed</h3>
      <p className="text-gray-600 mb-4">
        Complete your taste profile to get recipe recommendations tailored just for you.
      </p>
      <Button onClick={onSetupProfile}>Set Up Your Profile</Button>
    </div>
  );
}

export default function RecipePageView({
  // Data
  recipes,
  currentMealPlan,
  currentMealPlanRecipes,
  userProfile,
  userTasteProfile,
  userName,
  normalizedTier,

  // Loading states
  recipesLoading,

  // Search and filter state
  searchQuery,
  selectedCategory,
  selectedTags,
  sortBy,
  viewMode,
  currentPage,
  hasMoreRecipes,

  // Filter options
  availableCategories,
  availableTags,

  // Modal state
  selectedRecipe,
  replaceModalOpen,
  removeModalOpen,
  detailsModalOpen,
  recipeToReplace,
  recipeToRemove,

  // Computed values
  hasCurrentMealPlan,
  isRecipeInMealPlan,
  totalRecipesCount,

  // Extended state for original compatibility
  refreshing,
  paginationCategory,
  loadingMore,
  creating,
  addingToPlan,
  likedRecipes,
  activePreview,
  previewLoading,
  previewError,
  hasMore,
  error,
  personalizedRecipes = [],
  personalizedLoading = false,
  personalizedGroups = {},

  // Event handlers - Search and filter
  onSearch,
  onCategoryFilter,
  onTagFilter,
  onSortChange,
  onViewModeChange,
  onLoadMore,

  // Event handlers - Recipe interactions
  onRecipeClick,
  onReplaceRecipe,
  onConfirmReplace,
  onRemoveRecipe,
  onConfirmRemove,
  onAddToMealPlan,
  onGenerateNewPlan,

  // Event handlers - Modal management
  onCloseModals,

  // Event handlers - Navigation
  onGoToDashboard,
  onGoToShoppingList,
  onSetupProfile,

  // Extended event handlers for original compatibility
  onRefreshRecipes,
  onTrack,
  onCreatePlan,
  onLikeRecipe,
  onCreatePlanFromGrid,
  onAddToPlanFromModal,
  onPreviewPersonalization,
  onCreateCustomRecipe,
  onViewDetails,
}: RecipePageViewExtendedProps) {
  const USE_V2_SWIPER = FLAGS.useV2Swiper;
  const v2Recipes = Array.isArray(personalizedRecipes)
    ? personalizedRecipes
    : [];
  const isLoadingV2 = !!personalizedLoading;

  const showEmptyState = !recipesLoading && recipes.length === 0 && !userTasteProfile;

  return (
    <MealPlanErrorBoundary>
      <AiErrorBoundary>
        {/* Mobile view: Full-screen swipe interface without layout navigation (<md) */}
        <div className="md:hidden">
          {recipesLoading || (USE_V2_SWIPER && isLoadingV2) ? (
            <div className="p-4">
              <div className="mb-6">
                <AnimatedSkeleton
                  variant="shimmer"
                  className="mb-3 h-6 w-40 rounded"
                />
                <AnimatedSkeleton
                  variant="shimmer"
                  className="h-4 w-64 rounded"
                />
              </div>
              <AnimatedSkeleton
                variant="wave"
                className="aspect-[4/3] w-full rounded-lg"
              />
            </div>
          ) : error ? (
            <div className="p-4 text-center">
              <h3 className="mb-2 text-lg font-semibold text-red-600">
                Unable to load recipes
              </h3>
              <p className="mb-4 text-gray-600">
                {error?.message || "Something went wrong"}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          ) : (
            (() => {
              const recipesForSwiper = !USE_V2_SWIPER
                ? (recipes as any[]).map(toRecipeCardVMFromLegacy)
                : (v2Recipes as any[]).map(toRecipeCardVMFromV2);

              return (
                <RecipeSwiper
                  recipes={recipesForSwiper as any}
                  onSwipeRight={(r: any) => onTrack(r?.id, "liked")}
                  onSwipeLeft={(r: any) => onTrack(r?.id, "skipped")}
                  onSwipeUp={(r: any) => onTrack(r?.id, "saved")}
                  onSwipeDown={(r: any) => onTrack(r?.id, "maybe")}
                  onComplete={(sel: any[], saved: any[]) => onCreatePlan(sel)}
                  onBack={() => window.history.back()}
                  onRefresh={onRefreshRecipes}
                  refreshing={refreshing}
                  showProgress
                  enableHaptics
                />
              );
            })()
          )}
        </div>

        {/* Desktop view: Enhanced grid with new header and components (md+) */}
        <div className="hidden md:block">
          {error ? (
            <div className="mx-auto max-w-2xl p-6 text-center">
              <h3 className="mb-3 text-xl font-semibold text-red-600">
                Unable to load recipes
              </h3>
              <p className="mb-6 text-gray-600">
                {error?.message || "Something went wrong"}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="rounded-lg bg-blue-500 px-6 py-3 text-white transition-colors hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              <DiscoverHeader
                searchQuery={searchQuery}
                onSearchChange={onSearch}
                selectedCategory={selectedCategory}
                onCategoryChange={onCategoryFilter}
                selectedDietTags={selectedTags}
                onDietTagsChange={onTagFilter}
                onCreateCustomRecipe={onCreateCustomRecipe}
                showBreadcrumb={false}
                stickyOffset="top-0"
                userTasteProfile={userTasteProfile}
              />
              {/* Category Explorer Indicator */}
              {paginationCategory && !searchQuery.trim() && (
                <div className="mb-4 flex items-center justify-center">
                  <div className="rounded-full border border-coral-200 bg-coral-50 px-3 py-1 text-sm text-coral-700">
                    🔍 Exploring:{" "}
                    {paginationCategory.charAt(0).toUpperCase() +
                      paginationCategory.slice(1)}{" "}
                    recipes
                  </div>
                </div>
              )}

              {/* Enhanced Recipe Grid */}
              {showEmptyState ? (
                <PreferenceEmptyState onSetupProfile={onSetupProfile} />
              ) : (
                <RecipeGrid
                  recipes={recipes}
                  loading={recipesLoading}
                  likedRecipes={likedRecipes}
                  onLike={onLikeRecipe}
                  onAddToPlan={onAddToMealPlan}
                  onViewDetails={onViewDetails}
                  onLoadMore={onLoadMore}
                  hasMore={hasMore}
                  loadingMore={loadingMore}
                  onCreatePlan={onCreatePlanFromGrid}
                  creating={creating}
                />
              )}
            </>
          )}
        </div>

        {/* Recipe Detail Modal */}
        <RecipeDetailModal
          key={selectedRecipe?.id || (detailsModalOpen ? "open" : "closed")}
          recipe={selectedRecipe}
          isOpen={detailsModalOpen}
          onClose={onCloseModals}
          onAddToPlan={onAddToPlanFromModal}
          onPreviewPersonalize={onPreviewPersonalization}
          preview={activePreview}
          previewLoading={previewLoading}
          previewError={previewError}
          addingToPlan={addingToPlan}
        />
      </AiErrorBoundary>
    </MealPlanErrorBoundary>
  );
}